import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Modal, Form, Input, Popconfirm, Typography, Alert } from 'antd';
import { TeamOutlined, PlusOutlined, EditOutlined, DeleteOutlined, MailOutlined, PhoneOutlined, IdcardOutlined, UserOutlined } from '@ant-design/icons';
import AdminPageLayout from './layout/AdminPageLayout';
import useCustomerStore from '../../stores/customerStore';

const Customers = () => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState(null);
  const { customers, loading, fetchCustomers, createCustomer, updateCustomer, deleteCustomer, searchCustomers } = useCustomerStore();

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const showModal = (customer = null) => {
    setEditingCustomer(customer);
    setFormErrors(null); // Clear any previous errors
    if (customer) {
      form.setFieldsValue(customer);
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'ID Document',
      dataIndex: 'identification_file_url',
      key: 'identification_file_url',
      render: (url) => url ? <a href={url} target="_blank" rel="noopener noreferrer">View ID</a> : 'No ID',
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => showModal(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete Customer"
            description="Are you sure?"
            onConfirm={() => deleteCustomer(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <AdminPageLayout
      title="Customers"
      icon={<TeamOutlined />}
      subtitle="Manage your customers"
      actions={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
          Add Customer
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={customers}
        loading={loading}
        rowKey="id"
      />

      <Modal
        title={editingCustomer ? "Edit Customer" : "Add Customer"}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingCustomer(null);
          setFormErrors(null);
        }}
        width={600}
        confirmLoading={submitting}
        okText={submitting ? "Saving..." : "Save"}
      >
        {formErrors && (
          <Alert
            message="Error"
            description={
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {Object.entries(formErrors).map(([field, errors]) => (
                  <li key={field}>
                    <strong>{field}:</strong> {Array.isArray(errors) ? errors.join(', ') : errors}
                  </li>
                ))}
              </ul>
            }
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setFormErrors(null)}
          />
        )}
        
        <Form
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            setSubmitting(true);
            setFormErrors(null);
            try {
              const result = editingCustomer 
                ? await updateCustomer(editingCustomer.id, values)
                : await createCustomer(values);
              
              if (result.success) {
                setIsModalVisible(false);
                form.resetFields();
                setEditingCustomer(null);
              } else if (result.error) {
                // Set form errors from API response
                setFormErrors(result.error.response?.data || { general: 'An error occurred' });
              }
            } catch (error) {
              setFormErrors({ general: 'An unexpected error occurred' });
            } finally {
              setSubmitting(false);
            }
          }}
          requiredMark="optional"
        >
          <Form.Item
            name="name"
            label={
              <Space>
                <UserOutlined />
                Customer Name
              </Space>
            }
            rules={[{ required: true, message: 'Please input the customer name!' }]}
            tooltip="Full name of the customer"
          >
            <Input placeholder="Enter customer name" />
          </Form.Item>

          <Form.Item
            name="email"
            label={
              <Space>
                <MailOutlined />
                Email Address
              </Space>
            }
            rules={[
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
            tooltip="Primary contact email for the customer"
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            name="phone"
            label={
              <Space>
                <PhoneOutlined />
                Phone Number
              </Space>
            }
            tooltip="Contact phone number including country code"
          >
            <Input placeholder="Enter phone number (e.g., +39 123 456 7890)" />
          </Form.Item>

          <Form.Item
            name="identification_file_url"
            label={
              <Space>
                <IdcardOutlined />
                ID Document URL
              </Space>
            }
            tooltip="URL link to the customer's identification document"
          >
            <Input 
              placeholder="https://..." 
              suffix={
                editingCustomer?.identification_file_url && (
                  <Typography.Link 
                    href={editingCustomer.identification_file_url} 
                    target="_blank"
                    style={{ fontSize: '12px' }}
                  >
                    View current
                  </Typography.Link>
                )
              }
            />
            <Typography.Text type="secondary" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Provide a link to the customer's identification document
            </Typography.Text>
          </Form.Item>
        </Form>
      </Modal>
    </AdminPageLayout>
  );
};

export default Customers;
